import { PaymentMethodType } from '#types';
import {
	BoxProps,
	Divider,
	FormControl,
	InputLabel,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Select,
} from '@mui/material';
import { Icon } from '../../blocks/icons';
import { useId, useMemo } from 'react';
import { MuiPaymentMethodType } from '../../mui/MuiPaymentMethod/helpers';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import styles from './PaymentMethodSelect.module.scss';
import classNames from 'classnames';

export type PaymentMethodSelectProps = {
	paymentMethodType: PaymentMethodType | undefined;
	setPaymentMethodType: (
		paymentMethodType: PaymentMethodType | undefined
	) => void;
	cardConvenienceFeePercentage: number;
	achConvenienceFeePercentage: number;
	disabled?: boolean;
} & Pick<BoxProps, 'className'>;

const PaymentMethodSelect: React.FC<PaymentMethodSelectProps> = ( {
	paymentMethodType,
	setPaymentMethodType,
	cardConvenienceFeePercentage,
	achConvenienceFeePercentage,
	className,
	disabled,
} ) => {
	const { isMobile } = useDeviceDetection();
	const labelId = useId();
	const label = 'Payment method';
	const options = useMemo(
		() => [
			{
				type: MuiPaymentMethodType.CreditCard,
				label:
					( isMobile ? 'Card & Mobile Payment Apps' : 'Credit Card' ) +
					( cardConvenienceFeePercentage > 0
						? ` (${ cardConvenienceFeePercentage }%)`
						: '' ),
				value: PaymentMethodType.card,
			},
			{
				type: MuiPaymentMethodType.AutomatedClearingHouse,
				label: `Bank Account${
					achConvenienceFeePercentage > 0
						? ` (${ achConvenienceFeePercentage }%)`
						: ''
				}`,
				// it may end up not being us_bank_account, but we only want one "bank" option in this select
				value: PaymentMethodType.us_bank_account,
			},
		],
		[
			cardConvenienceFeePercentage,
			achConvenienceFeePercentage,
			isMobile
		]
	);

	const renderValue = useMemo(
		() => ( selected: PaymentMethodType ) => {
			if (
				[
					PaymentMethodType.card,
					PaymentMethodType.google_pay,
					PaymentMethodType.apple_pay,
				].includes( selected )
			) {
				return (
					options.find( ( option ) => option.value === PaymentMethodType.card )
						?.label || ''
				);
			} else if (
				[
					PaymentMethodType.us_bank_account,
					PaymentMethodType.acss_debit,
					PaymentMethodType.link,
				].includes( selected )
			) {
				return (
					options.find(
						( option ) => option.value === PaymentMethodType.us_bank_account
					)?.label || ''
				);
			}
			return 'Select a payment method';
		},
		[ options ]
	);

	return (
		<FormControl
			fullWidth
			className={ classNames( className, styles.container ) }
			disabled={ disabled }
		>
			<InputLabel id={ labelId } data-cy='payment-method-label'>
				{ label }
			</InputLabel>
			<Select
				label={ label }
				labelId={ labelId }
				fullWidth
				onChange={ ( e ) => {
					// to account for Link, we adjust undefined to be card
					const paymentMethodType =
						e.target.value === 'link'
							? PaymentMethodType.card
							: PaymentMethodType[
								e.target.value as keyof typeof PaymentMethodType
							  ];
					const newSelectedMethod = options.find(
						( paymentMethod ) =>
							typeof paymentMethod !== 'string' &&
							paymentMethod.value === paymentMethodType
					);
					if ( newSelectedMethod ) {
						setPaymentMethodType( newSelectedMethod.value );
					}
				} }
				value={ paymentMethodType || '' }
				renderValue={ renderValue }
			>
				{ options.map( ( option ) =>
					typeof option === 'string' ? (
						<Divider key='divider' />
					) : (
						<MenuItem key={ option.value } value={ option.value }>
							<ListItemIcon>
								<Icon
									type={ option.type }
									className={ [ styles.icon, styles[ option.type ] ].join( ' ' ) }
								/>
							</ListItemIcon>
							<ListItemText>{ option.label }</ListItemText>
						</MenuItem>
					)
				) }
			</Select>
		</FormControl>
	);
};

export default PaymentMethodSelect;
